<script module>
	const observer = new IntersectionObserver(entries => {
		entries
			.filter(entry => entry.isIntersecting)
			.forEach(entry => {
				entry.target.dispatchEvent(new Event('intersect'));
			});
	});
</script>

<script lang="ts">
	import { onMount } from 'svelte';
	const ZOOM0 = 10;
	const ZOOM1 = 5;
	const ZOOM2 = 1;
	let {
		heading,
		zoom1src,
		zoom1srcset,
		zoom2src,
		zoom2srcset,
		closeLabel,
		defaultzoom,
	} = $props();
	let steps = $state([ZOOM0]);
	if (zoom1src && zoom1srcset) {
		steps = [...steps, ZOOM1];
	}
	if (zoom2src && zoom2srcset) {
		steps = [...steps, ZOOM2];
	}
	let currentStep = $state(steps[defaultzoom]);
	let dialog: HTMLDialogElement;
	let { details } = $$slots;
	let defaultSlot: HTMLSlotElement;
	let img: HTMLImageElement | null;
	let imgsrc: string | null | undefined, imgsrcset: string | null | undefined;
	let zoom = $state(false);

	onMount(() => {
		img = defaultSlot.assignedElements()[0].querySelector('img');
		imgsrc = img?.getAttribute('src');
		imgsrcset = img?.getAttribute('srcset');

		if (img) {
			Array.from(img.parentNode?.querySelectorAll('source') || []).forEach(
				source => {
					source.remove();
				},
			);
		}

		observer.observe($host());
	});

	$host().addEventListener('click', e => {
		if (e.target.tagName == 'SUMMARY' && e.target.closest('details')?.open) {
			e.preventDefault();
		}
	});
	$host().addEventListener('intersect', () => {
		if (defaultzoom === 0) {
			loadImage(zoom1src, zoom1srcset);
		}
		if (defaultzoom === 1) {
			loadImage(zoom2src, zoom2srcset);
		}
	});

	const preventOverflow = (e: ToggleEvent) => {
		document.documentElement.classList.toggle(
			'overflow-hidden',
			e.newState === 'open',
		);
	};

	const loadImage = (src: string, srcset: string) =>
		new Promise<void>((resolve, reject) => {
			const img = document.createElement('img');
			if (img) {
				img.onload = () => {
					resolve();
				};
				img.src = src;
				img.srcset = srcset;
			}
		});

	$effect(() => {
		if (currentStep === ZOOM0 && imgsrc && imgsrcset) {
			loadImage(imgsrc, imgsrcset).then(() => {
				if (imgsrc && imgsrcset) {
					zoom = false;
					img?.setAttribute('src', imgsrc);
					img?.setAttribute('srcset', imgsrcset);
				}
			});
		}
		if (currentStep === ZOOM1) {
			loadImage(zoom1src, zoom1srcset).then(() => {
				if (zoom1src && zoom1srcset) {
					zoom = true;
					img?.setAttribute('src', zoom1src);
					img?.setAttribute('srcset', zoom1srcset);
				}
			});
		}
		if (currentStep === ZOOM2) {
			loadImage(zoom2src, zoom2srcset).then(() => {
				if (zoom2src && zoom2srcset) {
					zoom = true;
					img?.setAttribute('src', zoom2src);
					img?.setAttribute('srcset', zoom2srcset);
				}
			});
		}
	});
</script>

<svelte:options
	customElement={{
		tag: 'product-teaser',
		shadow: 'open',
		props: {
			heading: { reflect: false, attribute: 'heading' },
			zoom1src: { reflect: true, attribute: 'zoom1src' },
			zoom1srcset: { reflect: true, attribute: 'zoom1srcset' },
			zoom2src: { reflect: true, attribute: 'zoom2src' },
			zoom2srcset: { reflect: true, attribute: 'zoom2srcset' },
			defaultzoom: { reflect: false, attribute: 'defaultzoom', type: 'Number' },
		},
	}}
/>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
	class="wrapper"
	class:zoom
	onmouseenter={() => (currentStep = defaultzoom === 0 ? ZOOM1 : ZOOM2)}
	onmouseleave={() => (currentStep = steps[defaultzoom])}
>
	<div class="header">
		{#if details}
			<button
				class="button button-details"
				data-style="button"
				onclick={() => {
					dialog?.showModal();
				}}
			>
				{heading}
			</button>
		{:else}
			{heading}
		{/if}
	</div>
	<svelte:element this={'slot'} bind:this={defaultSlot} />

	<div class="controls">
		{#each steps as step (step)}
			<button
				type="button"
				aria-pressed={currentStep === step}
				onclick={() => (currentStep = step)}
			>
				1:{step}
			</button>
		{/each}
	</div>
</div>

{#if details}
	<dialog
		bind:this={dialog}
		ontoggle={preventOverflow}
		closedby="any"
		class="navigation overlay"
	>
		<div>
			<button
				type="button"
				class="button"
				data-style="text"
				onclick={() => dialog.close()}
			>
				{closeLabel}
			</button>
		</div>
		<slot name="details" />
	</dialog>
{/if}

<style lang="postcss">
	@import '../../css/blocks/navigation';
	@import '../../css/blocks/overlay';
	@import '../../css/blocks/button';

	.wrapper {
		display: grid;
		grid-template-rows: auto minmax(0, 1fr) auto;
		grid-template-columns: minmax(0, 1fr);
		overflow: clip;
		position: relative;
		height: 100%;
		aspect-ratio: 183 / 195;
		color: var(--color-gray-400);
		background-color: var(--color-gray-100);
		transition: color 100ms ease-out;

		@container (min-width: 300px) {
			aspect-ratio: 370/ 240;
		}

		@container (min-width: 400px) {
			aspect-ratio: 918 / 534;
		}

		@media (hover: none) {
			.button-details {
				backdrop-filter: none;
			}
		}

		@media (hover: hover) {
			&:hover {
				cursor: pointer;

				.button-details {
					--button-bg-color: var(--color-red);
					--button-bg-opacity: 1;
					backdrop-filter: none;
				}

				[aria-pressed='true'] {
					--button-bg-color: var(--color-red);
				}
			}
		}
	}

	.button-details.button-details {
		grid-row: 1;
		padding: var(--gutter);
		grid-column: 1;
		isolation: isolate;
		cursor: pointer;
		padding-block: 2px;

		&::after {
			content: '';
			inset: 0;
			position: absolute;
		}

		@screen md {
			padding-block: inherit;
		}
	}

	:global(::slotted(picture)) {
		grid-row: 1 / -1;
		pointer-events: none;
		margin: 12%;
		grid-column: 1;
	}
	:global(.zoom ::slotted(picture)) {
		margin: 0;
	}

	.header {
		padding: var(--gutter);
		grid-row: 1;
		grid-column: 1;
	}

	.controls {
		display: flex;
		padding: var(--gutter);
		grid-column: 1;
		pointer-events: none;
		grid-row: 3;
		z-index: 1;
		gap: 1px;

		@screen md {
			gap: 0.625rem;
		}

		&:hover button {
			--button-bg-color: var(--color-gray-400);
		}

		button {
			appearance: none;
			border-color: transparent;
			border-radius: 3px;
			font-size: 0.625rem;
			color: var(--color-white);
			backdrop-filter: blur(var(--blur, 4px))
				brightness(var(--button-bg-brightness, -100%));
			background-color: oklch(
				from var(--button-bg-color, var(--color-gray-400)) l c h /
					var(--button-bg-opacity, 0.5)
			);
			transition: all 100ms ease-out;
			pointer-events: auto;
			cursor: pointer;

			&:hover {
				--button-bg-color: var(--color-red);
				--button-bg-opacity: 0.5;
			}
		}
		button[aria-pressed='true'] {
			--button-bg-opacity: 1;
			--button-bg-color: var(--color-red);
		}
	}
	.navigation {
		.button {
			@screen md {
				margin: -6px;
			}
		}
	}
</style>
